import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import api from "../../services/api";

interface HolidayManagementProps {
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

const HolidayManagement: React.FC<HolidayManagementProps> = ({
  onSuccess,
  onError,
}) => {
  const [holidays, setHolidays] = useState<any[]>([]);
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [openDialog, setOpenDialog] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<any>(null);
  const [holidayForm, setHolidayForm] = useState({
    name: "",
    holidayDate: "",
    description: "",
  });

  const loadHolidays = async (selectedYear: number) => {
    try {
      const response = await api.get("/admin/holidays", {
        params: { year: selectedYear },
      });
      setHolidays(response.data || []);
    } catch (error) {
      onError("Failed to load holidays");
    }
  };

  useEffect(() => {
    loadHolidays(year);
  }, [year]);

  const resetForm = () => {
    setHolidayForm({
      name: "",
      holidayDate: "",
      description: "",
    });
    setEditingHoliday(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    setOpenDialog(true);
  };

  const handleOpenEdit = (holiday: any) => {
    setEditingHoliday(holiday);
    setHolidayForm({
      name: holiday.name || "",
      holidayDate: String(holiday.holiday_date).split("T")[0],
      description: holiday.description || "",
    });
    setOpenDialog(true);
  };

  const handleSaveHoliday = async () => {
    if (!holidayForm.name || !holidayForm.holidayDate) {
      onError("Holiday name and date are required");
      return;
    }

    try {
      if (editingHoliday) {
        await api.put(`/admin/holidays/${editingHoliday.id}`, holidayForm);
        onSuccess("Holiday updated successfully");
      } else {
        await api.post("/admin/holidays", holidayForm);
        onSuccess("Holiday created successfully");
      }

      setOpenDialog(false);
      resetForm();
      loadHolidays(year);
    } catch (error: any) {
      onError(error?.response?.data?.error || "Failed to save holiday");
    }
  };

  const handleDeleteHoliday = async (holidayId: number) => {
    if (!window.confirm("Are you sure you want to delete this holiday?")) {
      return;
    }

    try {
      await api.delete(`/admin/holidays/${holidayId}`);
      onSuccess("Holiday deleted successfully");
      loadHolidays(year);
    } catch (error: any) {
      onError(error?.response?.data?.error || "Failed to delete holiday");
    }
  };

  const yearOptions = [year - 1, year, year + 1, year + 2, year + 3];

  return (
    <Box>
      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="h6">Yearly Holiday Management</Typography>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            select
            label="Year"
            size="small"
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value, 10))}
            sx={{ minWidth: 120 }}
          >
            {yearOptions.map((yearOption) => (
              <MenuItem key={yearOption} value={yearOption}>
                {yearOption}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
          >
            Add Holiday
          </Button>
        </Box>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {holidays.map((holiday) => (
              <TableRow key={holiday.id}>
                <TableCell>
                  {new Date(holiday.holiday_date).toLocaleDateString()}
                </TableCell>
                <TableCell>{holiday.name}</TableCell>
                <TableCell>{holiday.description || "-"}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpenEdit(holiday)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDeleteHoliday(holiday.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {holidays.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No holidays configured for {year}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>
          {editingHoliday ? "Edit Holiday" : "Add Holiday"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Holiday Name"
            fullWidth
            value={holidayForm.name}
            onChange={(e) =>
              setHolidayForm((prev) => ({ ...prev, name: e.target.value }))
            }
          />
          <TextField
            margin="dense"
            label="Holiday Date"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={holidayForm.holidayDate}
            onChange={(e) =>
              setHolidayForm((prev) => ({
                ...prev,
                holidayDate: e.target.value,
              }))
            }
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={holidayForm.description}
            onChange={(e) =>
              setHolidayForm((prev) => ({
                ...prev,
                description: e.target.value,
              }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenDialog(false);
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleSaveHoliday}>
            {editingHoliday ? "Save" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HolidayManagement;
